import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { verifyQRAuth } from '../services/passkeyService';

/**
 * PasskeyQRAuth Component
 * Handles phone-side authentication after scanning QR code from desktop
 */
export const PasskeyQRAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    // Parse QR code data from URL
    const qrParam = searchParams.get('qr');
    if (qrParam) {
      try {
        const data = JSON.parse(decodeURIComponent(qrParam));
        setQrData(data);
      } catch (err) {
        setError('Invalid QR code data');
      }
    } else {
      setError('No QR code data found');
    }
  }, [searchParams]);

  const handleAuthenticate = async () => {
    if (!qrData) {
      setError('No QR code data available');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let credential;

      if (qrData.mode === 'login') {
        // Perform login authentication
        const optionsResponse = await fetch(`${qrData.url}/api/passkey/login/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: qrData.email }),
        });

        if (!optionsResponse.ok) {
          throw new Error('Failed to get authentication options');
        }

        const { challengeId, ...options } = await optionsResponse.json();

        // Use the challenge from QR data if available
        if (qrData.challengeId) {
          options.challenge = qrData.challengeId;
        }

        credential = await startAuthentication(options);
      } else if (qrData.mode === 'add-passkey') {
        // Add passkey to existing user - use WebAuthn options from QR session
        // The backend already generated the options when creating QR session
        const optionsResponse = await fetch(`${qrData.url}/api/passkey/qr/options/${qrData.sessionId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!optionsResponse.ok) {
          throw new Error('Failed to get passkey options');
        }

        const options = await optionsResponse.json();
        credential = await startRegistration(options);
      } else {
        // Perform registration (new user)
        const optionsResponse = await fetch(`${qrData.url}/api/passkey/register/new-user/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: qrData.email,
            displayName: qrData.email
          }),
        });

        if (!optionsResponse.ok) {
          throw new Error('Failed to get registration options');
        }

        const options = await optionsResponse.json();
        credential = await startRegistration(options);
      }

      // Send credential to server via QR verify endpoint
      await verifyQRAuth(qrData.sessionId, qrData.challengeId, credential);

      setSuccess(true);
    } catch (err) {
      console.error('QR Auth error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (error && !qrData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brown-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-brown-800 mb-4">Invalid QR Code</h2>
          <p className="text-brown-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    const successMessage = qrData?.mode === 'add-passkey'
      ? 'Passkey added successfully! Check your desktop.'
      : 'Your desktop is now logged in. You can close this page.';

    return (
      <div className="min-h-screen flex items-center justify-center bg-brown-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h2 className="text-xl font-bold text-green-800 mb-4">Success!</h2>
          <p className="text-brown-600 mb-4">
            {successMessage}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-green-700">
              ✓ {qrData?.mode === 'add-passkey' ? 'Passkey registered' : 'Desktop authentication complete'}<br/>
              ✓ You can return to your computer
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brown-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">📱</div>
          <h2 className="text-2xl font-bold text-brown-800 mb-2">
            Authenticate Desktop Login
          </h2>
          <p className="text-brown-600">
            Use your phone's biometrics to log in on your desktop
          </p>
        </div>

        {qrData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="text-sm text-blue-800 mb-2">
              <strong>Action:</strong> {
                qrData.mode === 'login' ? 'Sign In' :
                qrData.mode === 'add-passkey' ? 'Add Passkey' :
                'Sign Up'
              }
            </div>
            {qrData.email && (
              <div className="text-sm text-blue-800">
                <strong>Email:</strong> {qrData.email}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleAuthenticate}
            disabled={loading || !qrData}
            className="w-full bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Authenticating...' : 'Authenticate with Face ID/Touch ID'}
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-300 hover:bg-gray-400 text-brown-800 font-bold py-2 px-4 rounded-full"
          >
            Cancel
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-brown-500">
            Your biometric data stays on this device and is never shared
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasskeyQRAuth;
